/**
 * Property type — House vs Apartment/Unit (or Any).
 *
 * Why this exists:
 *   A "A$900k–1.5M" budget lands completely different properties depending
 *   on whether the user is thinking House vs Apartment. Users told us the
 *   price output wasn't believable without this context. This axis is tiny
 *   — three states, three pills — but lets the budget-format helper quote
 *   numbers that match the user's actual search.
 *
 * Design rules:
 *   - Mirrors the same store/subscribe pattern as `budget-band.ts` and
 *     `purchase-mode.ts`. Pure state, no DOM here.
 *   - Default = "any" so the user's first glance is not over-filtered.
 *   - Persisted in localStorage.
 *   - Does NOT influence scoring or the shortlist — it only reshapes the
 *     budget-label text the user sees. Any future scoring tweak can be added
 *     later without reshaping this module.
 */

export type PropertyType = "any" | "house" | "apartment";

const STORAGE_KEY = "hyperlocal-property-type";

let current: PropertyType = loadInitial();
const listeners = new Set<(p: PropertyType) => void>();

function loadInitial(): PropertyType {
  try {
    if (typeof localStorage !== "undefined") {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === "any" || v === "house" || v === "apartment") return v;
    }
  } catch {
    /* storage blocked */
  }
  return "any";
}

export function getPropertyType(): PropertyType {
  return current;
}

export function setPropertyType(p: PropertyType): void {
  if (p === current) return;
  current = p;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, p);
    }
  } catch {
    /* ignore */
  }
  for (const fn of Array.from(listeners)) {
    try {
      fn(p);
    } catch {
      /* broken subscriber must not break the rest */
    }
  }
}

export function subscribePropertyType(
  fn: (p: PropertyType) => void
): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getPropertyTypeLabel(p: PropertyType = current): string {
  if (p === "house") return "House";
  if (p === "apartment") return "Apartment";
  return "Any";
}
